import React, { useEffect, useState } from "react";
import { Loader2, Award, Info } from "lucide-react";
import api from "../lib/api";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import GlossaryText from "./GlossaryText";

const GRADE_COLORS = {
  A: { bg: "#5C8D73", fg: "#FFFFFF" },
  B: { bg: "#547A8F", fg: "#FFFFFF" },
  C: { bg: "#D18E63", fg: "#FFFFFF" },
  D: { bg: "#C66B59", fg: "#FFFFFF" },
};

const VERDICT_COLORS = {
  pass: "#5C8D73",
  borderline: "#D18E63",
  fail: "#C66B59",
  unknown: "#6B7068",
};

const VERDICT_LABEL = {
  pass: "Pass",
  borderline: "Borderline",
  fail: "Fail",
  unknown: "—",
};

/**
 * CAN SLIM scorecard. Two render modes via `variant`:
 *   - "compact"  → sidebar card with grade + 7 pillar dots (default)
 *   - "detailed" → full-width tab content with each pillar expanded
 */
export default function CanSlimCard({ symbol, variant = "compact" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError(false);
    api
      .get(`/canslim/${symbol}`)
      .then((r) => {
        if (!cancel) setData(r.data);
      })
      .catch(() => {
        if (!cancel) setError(true);
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [symbol]);

  if (loading) {
    return (
      <div
        className="rounded-2xl bg-white border border-[#EAE6E1] p-6 flex items-center gap-2 text-sm text-[#6B7068]"
        data-testid="canslim-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Scoring CAN SLIM...
      </div>
    );
  }
  if (error || !data) return null;

  const grade = GRADE_COLORS[data.grade] || GRADE_COLORS.D;

  if (variant === "compact") {
    return (
      <div
        className="rounded-2xl bg-white border border-[#EAE6E1] p-6"
        data-testid="canslim-card"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068] mb-1 flex items-center gap-1.5">
              <Award className="w-3 h-3 text-[#D18E63]" strokeWidth={1.75} />
              CAN SLIM Score
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      data-testid="canslim-info-tooltip"
                      className="text-[#6B7068] hover:text-[#2A2F2B]"
                      aria-label="What is CAN SLIM?"
                    >
                      <Info className="w-3 h-3" strokeWidth={1.75} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs">
                    William O'Neil's 7-pillar checklist for spotting leading
                    growth stocks. Each pillar scored 0–10. Higher = stronger
                    candidate.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="font-heading text-2xl tracking-tight text-[#2A2F2B]">
              {data.total}
              <span className="text-[#6B7068] text-base"> / {data.max}</span>
            </div>
            <div className="text-xs text-[#6B7068] mt-0.5 font-mono-ticker">
              {data.percent}% strength
            </div>
          </div>
          <div
            data-testid="canslim-grade-badge"
            className="w-14 h-14 rounded-2xl flex items-center justify-center font-heading text-3xl font-bold"
            style={{ backgroundColor: grade.bg, color: grade.fg }}
          >
            {data.grade}
          </div>
        </div>

        {/* 7 pillar dots */}
        <div className="space-y-2">
          {data.pillars.map((p) => (
            <div
              key={p.key}
              data-testid={`canslim-pillar-${p.key}`}
              className="flex items-center gap-2"
            >
              <span
                className="w-6 h-6 rounded-md flex items-center justify-center font-mono-ticker text-xs font-bold text-white"
                style={{ backgroundColor: VERDICT_COLORS[p.verdict] }}
              >
                {p.key}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-[#2A2F2B] truncate">{p.title}</div>
                <div className="text-[10px] text-[#6B7068] font-mono-ticker truncate">
                  {p.value}
                </div>
              </div>
              <div
                className="w-16 h-1.5 rounded-full bg-[#EAE6E1] overflow-hidden"
                aria-label={`Score ${p.score}/10`}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${(p.score / 10) * 100}%`,
                    backgroundColor: VERDICT_COLORS[p.verdict],
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-[#6B7068] mt-4 pt-3 border-t border-[#EAE6E1]">
          Educational scoring based on Finviz + market data. Not financial advice.
        </p>
      </div>
    );
  }

  // Detailed variant
  return (
    <div className="space-y-4" data-testid="canslim-detail">
      <div className="rounded-2xl bg-white border border-[#EAE6E1] p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068] mb-1 flex items-center gap-1.5">
              <Award className="w-3 h-3 text-[#D18E63]" strokeWidth={1.75} />
              William O'Neil's CAN SLIM Methodology
            </div>
            <div className="font-heading text-3xl tracking-tight text-[#2A2F2B]">
              {data.company} scored {data.total} / {data.max}
            </div>
            <p className="text-sm text-[#6B7068] mt-2 leading-relaxed max-w-xl">
              CAN SLIM is a 7-pillar checklist used by IBD's top-performing
              stock pickers. We score each pillar 0–10 using Finviz fundamentals
              and live price data. Aim for grade A or B candidates in
              uptrending markets.
            </p>
          </div>
          <div
            data-testid="canslim-detail-grade"
            className="w-24 h-24 rounded-3xl flex flex-col items-center justify-center"
            style={{ backgroundColor: grade.bg, color: grade.fg }}
          >
            <div className="font-heading text-5xl font-bold leading-none">
              {data.grade}
            </div>
            <div className="text-xs font-mono-ticker mt-1 opacity-80">
              {data.percent}%
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {data.pillars.map((p) => (
          <div
            key={p.key}
            data-testid={`canslim-detail-pillar-${p.key}`}
            className="rounded-2xl bg-white border border-[#EAE6E1] p-5"
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center font-mono-ticker text-lg font-bold text-white"
                  style={{ backgroundColor: VERDICT_COLORS[p.verdict] }}
                >
                  {p.key}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#6B7068]">
                    Pillar {p.key}
                  </div>
                  <div className="font-heading text-lg text-[#2A2F2B] tracking-tight">
                    {p.title}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono-ticker text-2xl text-[#2A2F2B]">
                  {p.score}
                  <span className="text-[#6B7068] text-sm"> /10</span>
                </div>
                <div
                  className="text-[10px] uppercase tracking-[0.2em] font-semibold mt-0.5"
                  style={{ color: VERDICT_COLORS[p.verdict] }}
                >
                  {VERDICT_LABEL[p.verdict] || p.verdict}
                </div>
              </div>
            </div>
            <div
              className="text-sm font-mono-ticker text-[#2A2F2B] mb-2"
              data-testid={`canslim-detail-pillar-${p.key}-value`}
            >
              {p.value}
            </div>
            <p className="text-sm text-[#6B7068] leading-relaxed">
              <GlossaryText>{p.explanation}</GlossaryText>
            </p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl bg-[#547A8F]/5 border border-[#547A8F]/20 p-5">
        <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#547A8F] mb-2">
          How to read this
        </div>
        <ul className="text-sm text-[#2A2F2B] space-y-1.5 leading-relaxed list-disc list-inside">
          <li>
            <strong>A (56-70)</strong> — Strong CAN SLIM candidate. Worth a
            deeper look.
          </li>
          <li>
            <strong>B (42-55)</strong> — Solid; missing one or two pillars.
          </li>
          <li>
            <strong>C (28-41)</strong> — Mixed. Most pillars failing.
          </li>
          <li>
            <strong>D (&lt;28)</strong> — Poor fit for the CAN SLIM system.
          </li>
        </ul>
        <p className="text-[10px] text-[#6B7068] mt-3 pt-2 border-t border-[#547A8F]/20">
          Source: William O'Neil, <em>How to Make Money in Stocks</em>. This is
          an educational tool, not investment advice. Past performance doesn't
          predict future returns.
        </p>
      </div>
    </div>
  );
}
